import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Compass, LineChart, UserCog, ShieldAlert, Target, ScrollText, Heart, Brain,
  Send, Loader2, Sparkles, CheckCircle2, CircleDashed
} from 'lucide-react';
import './Derek.css';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  Compass, LineChart, UserCog, ShieldAlert, Target, ScrollText, Heart, Brain,
};

const TYPING_MESSAGES = [
  'Learning the business…',
  'Connecting systems…',
  'Awaiting input…',
  'Building understanding…',
  'Observing every signal…',
];

// ── Typing-cycle hook (letter-by-letter, rotating phrases) ──
function useTypingCycle(phrases, charDelay = 55, holdMs = 1800) {
  const [out, setOut] = useState('');
  const idxRef = useRef(0);
  const timerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const cycle = async () => {
      while (!cancelled) {
        const phrase = phrases[idxRef.current % phrases.length];
        // Type in
        for (let i = 1; i <= phrase.length; i++) {
          if (cancelled) return;
          setOut(phrase.slice(0, i));
          await new Promise((r) => { timerRef.current = setTimeout(r, charDelay); });
        }
        await new Promise((r) => { timerRef.current = setTimeout(r, holdMs); });
        // Type out
        for (let i = phrase.length; i >= 0; i--) {
          if (cancelled) return;
          setOut(phrase.slice(0, i));
          await new Promise((r) => { timerRef.current = setTimeout(r, charDelay * 0.5); });
        }
        idxRef.current += 1;
      }
    };
    cycle();
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);
  return out;
}

// ── Particle field (CSS-only animated grid) ──
function ParticleField() {
  return (
    <div className="dk-particles" aria-hidden="true">
      <div className="dk-grid-bg" />
      <div className="dk-scan" />
      {Array.from({ length: 32 }).map((_, i) => (
        <span
          key={i}
          className="dk-dot"
          style={{
            left: `${(i * 317) % 100}%`,
            top: `${(i * 193) % 100}%`,
            animationDelay: `${(i % 10) * 0.4}s`,
            animationDuration: `${6 + (i % 5) * 2}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── The Derek core orb with cursor-following glow ──
function DerekCore({ thinking, onSubmit }) {
  const subtitle = useTypingCycle(TYPING_MESSAGES);
  const [msg, setMsg] = useState('');
  const coreRef = useRef(null);
  const orbRef = useRef(null);

  useEffect(() => {
    const onMove = (e) => {
      const el = orbRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      const tx = Math.max(-1, Math.min(1, dx)) * 8;
      const ty = Math.max(-1, Math.min(1, dy)) * 8;
      el.style.setProperty('--dk-tx', `${tx}px`);
      el.style.setProperty('--dk-ty', `${ty}px`);
      el.style.setProperty('--dk-cx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--dk-cy', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handle = (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    onSubmit(msg.trim());
    setMsg('');
  };

  return (
    <div className="dk-core-wrap" ref={coreRef}>
      <div className={`dk-robot-stage ${thinking ? 'dk-robot-thinking' : ''}`} ref={orbRef} data-testid="dk-orb">
        {/* Scanner rings */}
        <div className="dk-orb-ring dk-orb-ring-1" />
        <div className="dk-orb-ring dk-orb-ring-2" />
        <div className="dk-orb-ring dk-orb-ring-3" />
        <div className="dk-floor" />

        {/* Glowing orb */}
        <div className="dk-orb">
          <div className="dk-orb-core">
            <div className="dk-orb-inner" />
            <div className="dk-orb-shine" />
          </div>
          {/* Neural mesh lines */}
          <svg className="dk-orb-mesh" viewBox="0 0 200 200" aria-hidden="true">
            {Array.from({ length: 12 }).map((_, i) => {
              const a = (i / 12) * Math.PI * 2;
              return (
                <line
                  key={i}
                  x1={100 + Math.cos(a) * 50}
                  y1={100 + Math.sin(a) * 50}
                  x2={100 + Math.cos(a) * 92}
                  y2={100 + Math.sin(a) * 92}
                  stroke="rgba(201, 232, 101, 0.25)"
                  strokeWidth="0.8"
                />
              );
            })}
          </svg>
        </div>
      </div>

      <div className="dk-core-meta">
        <div className="dk-core-eyebrow">AI Overseer</div>
        <h1 className="dk-core-title">Derek</h1>
        <div className="dk-core-subtitle" data-testid="dk-core-subtitle">
          {subtitle}<span className="dk-caret">|</span>
        </div>
      </div>

      <form className="dk-input-wrap" onSubmit={handle} data-testid="dk-chat-form">
        <input
          type="text"
          className="dk-input"
          placeholder="Ask Derek anything about the business…"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          data-testid="dk-chat-input"
        />
        <button
          type="submit"
          className="dk-activate"
          disabled={thinking || !msg.trim()}
          data-testid="dk-activate-btn"
        >
          {thinking ? <Loader2 size={16} className="dk-spin" /> : <Send size={16} />}
          <span>Ask Derek</span>
        </button>
      </form>
    </div>
  );
}

// ── Memory panel (right) ──
function DerekMemoryPanel({ knowledge, progress }) {
  const percent = progress?.percent ?? knowledge?.training_progress ?? 10;
  const label = progress?.label || 'Derek Training Progress';
  const message = progress?.message || 'Derek is becoming more accurate as knowledge grows.';
  const count = progress?.count;
  return (
    <aside className="dk-panel dk-memory" data-testid="dk-memory">
      <header className="dk-panel-head">
        <Brain size={16} />
        <h3>Knowledge Base</h3>
      </header>

      <div className="dk-memory-scroll">
        <section className="dk-memory-section">
          <div className="dk-memory-label">Company</div>
          <div className="dk-memory-item">
            <strong>{knowledge?.company?.name || 'NicheCom'}</strong>
            <p>{knowledge?.company?.positioning}</p>
          </div>
        </section>

        <section className="dk-memory-section">
          <div className="dk-memory-label">Mission</div>
          <p className="dk-memory-item-text">{knowledge?.company?.mission}</p>
        </section>

        <section className="dk-memory-section">
          <div className="dk-memory-label">Products</div>
          <ul className="dk-memory-list">
            {(knowledge?.products || []).map((p) => (
              <li key={p.name} className="dk-memory-product">
                <strong>{p.name}</strong>
                <span>{p.desc}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="dk-training" data-testid="dk-training">
        <div className="dk-training-head">
          <span>{label}</span>
          <strong>{percent}%</strong>
        </div>
        <div className="dk-training-bar">
          <div className="dk-training-fill" style={{ width: `${percent}%` }} />
        </div>
        <div className="dk-training-meta">
          {typeof count === 'number' && (
            <span className="dk-training-count" data-testid="dk-training-count">
              {count} memory {count === 1 ? 'entry' : 'entries'}
            </span>
          )}
          <span className="dk-training-msg">{message}</span>
        </div>
      </div>
    </aside>
  );
}

// ── Capabilities grid (left) ──
function DerekCapabilities({ capabilities, onPick }) {
  return (
    <aside className="dk-panel dk-capabilities" data-testid="dk-capabilities">
      <header className="dk-panel-head">
        <Sparkles size={16} />
        <h3>Capabilities</h3>
      </header>
      <div className="dk-cap-grid">
        {(capabilities || []).map((c) => {
          const Icon = ICON_MAP[c.icon] || Sparkles;
          return (
            <button
              key={c.key}
              className="dk-cap-card"
              onClick={() => onPick(c)}
              data-testid={`dk-cap-${c.key}`}
            >
              <div className="dk-cap-icon"><Icon size={18} /></div>
              <div className="dk-cap-label">{c.label}</div>
              <div className="dk-cap-desc">{c.desc}</div>
              <div className="dk-cap-glow" />
            </button>
          );
        })}
      </div>
    </aside>
  );
}

// ── Status bar (top) ──
function DerekStatusBar({ status }) {
  return (
    <div className="dk-status" data-testid="dk-status-bar">
      <div className="dk-status-left">
        <span className="dk-status-pulse" />
        <span className="dk-status-label">Status: {status?.label || 'Online (Training Mode)'}</span>
      </div>
      <div className="dk-status-right">
        {(status?.systems || []).map((s) => (
          <span
            key={s.key}
            className={`dk-sys dk-sys-${s.status}`}
            data-testid={`dk-sys-${s.key}`}
          >
            {s.status === 'connected' ? <CheckCircle2 size={12} /> : <CircleDashed size={12} />}
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Reply panel (shows latest exchange) ──
function DerekReply({ exchange, thinking }) {
  if (!exchange && !thinking) return null;
  return (
    <div className="dk-reply" data-testid="dk-reply" role="status" aria-live="polite">
      {exchange && (
        <div className="dk-reply-you" data-testid="dk-reply-you">
          <span className="dk-reply-tag">You</span>
          <span className="dk-reply-body">{exchange.message}</span>
        </div>
      )}
      {thinking ? (
        <div className="dk-reply-derek dk-reply-thinking" data-testid="dk-reply-thinking">
          <span className="dk-reply-tag">Derek</span>
          <span className="dk-reply-body">
            <span className="dk-thinking">
              <span>Derek is thinking</span>
              <span className="dk-thinking-dot" />
              <span className="dk-thinking-dot" />
              <span className="dk-thinking-dot" />
            </span>
          </span>
        </div>
      ) : (
        exchange?.reply && (
          <div className="dk-reply-derek" data-testid="dk-reply-derek">
            <span className="dk-reply-tag">Derek</span>
            <span className="dk-reply-body">{exchange.reply}</span>
          </div>
        )
      )}
    </div>
  );
}

// ── Root ──
export default function Derek() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booted, setBooted] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [toast, setToast] = useState(null);
  const [exchange, setExchange] = useState(null); // { message, reply }
  const [progress, setProgress] = useState(null); // { count, percent, label, message }

  const loadProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/derek/memory/progress`);
      setProgress(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/derek/status`);
        setStatus(res.data);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
    loadProgress();
    // small delay for the cinematic fade-in
    const t = setTimeout(() => setBooted(true), 350);
    return () => clearTimeout(t);
  }, [loadProgress]);

  const showToast = useCallback((text) => {
    setToast(text);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const sendChat = useCallback(async (message) => {
    setThinking(true);
    setExchange({ message, reply: null });
    try {
      const res = await axios.post(`${API}/api/derek/chat`, { message });
      setExchange({ message, reply: res.data.reply });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Derek is temporarily unavailable.';
      setExchange({ message, reply: detail });
    } finally {
      setThinking(false);
    }
  }, []);

  const onCapability = useCallback((cap) => {
    showToast(`Coming soon — Derek is learning ${cap.label}.`);
  }, [showToast]);

  return (
    <div className={`dk-wrap ${booted ? 'dk-booted' : ''}`} data-testid="derek-wrap">
      <ParticleField />
      <DerekStatusBar status={status} />

      {loading ? (
        <div className="dk-loading"><Loader2 className="dk-spin" size={22} /> Waking Derek…</div>
      ) : (
        <div className="dk-stage">
          <DerekCapabilities
            capabilities={status?.capabilities}
            onPick={onCapability}
          />
          <div className="dk-core-column">
            <DerekCore thinking={thinking} onSubmit={sendChat} />
            <DerekReply exchange={exchange} thinking={thinking} />
          </div>
          <DerekMemoryPanel knowledge={status?.knowledge} progress={progress} />
        </div>
      )}

      {toast && (
        <div className="dk-toast" data-testid="dk-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
